import { Location } from '@angular/common';
import { Injectable, OnDestroy, inject } from '@angular/core';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { Timestamp, serverTimestamp } from '@angular/fire/firestore';
import { ActivatedRoute, Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import moment from 'moment';
import { NGXLogger } from 'ngx-logger';
import { ToastrService } from 'ngx-toastr';
import { intersects } from 'radash';
import {
  EMPTY,
  Observable,
  Subject,
  catchError,
  debounceTime,
  from,
  map,
  mergeMap,
  of,
  switchMap,
  take,
  takeUntil,
  withLatestFrom,
} from 'rxjs';
import { GradeLesson } from '../../models/grade-lesson.school.model';
import { Group, GroupType } from '../../models/group.model';
import { SchoolMemberType } from '../../models/member.school.model';
import { UserModel } from '../../models/user.model';
import { SchoolGradeLessonService } from '../../services/grade-lesson.school.service';
import * as gradeLessonActions from '../actions/grade-lesson.school.actions';
import * as groupActions from '../actions/group.actions';
import * as userActions from '../actions/user.actions';
import { AppState } from '../appstate';
import * as gradeLessonSelector from '../selectors/grade-lesson.school.selector';
import * as groupSelector from '../selectors/group.selector';

@Injectable()
export class SchoolGradeLessonEffects implements OnDestroy {
  actions$ = inject(Actions);
  store = inject(Store<AppState>);
  gradeLessonService: SchoolGradeLessonService = inject(
    SchoolGradeLessonService,
  );
  toastr: ToastrService = inject(ToastrService);
  logger: NGXLogger = inject(NGXLogger);
  afs: AngularFirestore = inject(AngularFirestore);
  router: Router = inject(Router);
  route: ActivatedRoute = inject(ActivatedRoute);
  location: Location = inject(Location);

  user: UserModel | null = null;

  private logout$ = new Subject<void>();

  constructor() {
    this.store
      .select((state) => state.user.user)
      .pipe(takeUntil(this.logout$))
      .subscribe((user) => {
        this.user = user;
      });
  }

  ngOnDestroy(): void {
    this.logout$.next();
    this.logout$.complete();
  }

  // Effect to fetch Pending GradeLessons
  getGradeLessons$ = createEffect(() =>
    this.actions$.pipe(
      ofType(gradeLessonActions.getGradeLessons),
      debounceTime(500),
      mergeMap(
        ({ groupId, gradeId, academicYear, status, publishBy, lastRecord }) =>
          this.gradeLessonService
            .getGradeLessonsByGroup(
              groupId,
              gradeId,
              academicYear,
              status,
              publishBy,
              10,
              lastRecord,
            )
            .pipe(
              map((lessons: GradeLesson[]) => {
                this.logger.debug(
                  `Fetched ${lessons.length} lessons for groupId: ${groupId}, gradeId: ${gradeId}, academicYear: ${academicYear}, status: ${status}`,
                );
                return gradeLessonActions.addGradeLessonsToStore({ lessons });
              }),
              catchError((error) => {
                this.logger.error(`getGradeLessons$: ${error}`);
                this.toastr.error(
                  'An error occurred. Please try again later.',
                  'Error Fetching Grade Lessons',
                );
                return of(gradeLessonActions.getGradeLessonsFailure({ error }));
              }),
            ),
      ),
    ),
  );

  getGradeLesson$ = createEffect(() =>
    this.actions$.pipe(
      ofType(gradeLessonActions.getGradeLesson),
      debounceTime(500),
      switchMap(({ groupId, gradeLessonId }) => {
        this.logger.debug(
          `getGradeLesson$: Fetching lesson with ID ${gradeLessonId} for group ${groupId}`,
        );
        return from(
          this.afs
            .collection('groups')
            .doc(groupId)
            .collection('lessons')
            .doc(gradeLessonId)
            .get(),
        ).pipe(
          map((doc) => {
            if (!doc.exists) {
              this.logger.error('getGradeLesson$: GradeLesson not found');
              return gradeLessonActions.getGradeLessonFailure({
                error: {
                  title: 'Not Found',
                  message: 'Lesson could not be fetched.',
                },
              });
            }

            const lesson = doc.data() as GradeLesson;
            lesson.id = doc.id;
            return gradeLessonActions.addGradeLessonToStore({ lesson });
          }),
          // 3. Catch errors locally so the main Effect stream doesn't die
          catchError((error) => {
            this.logger.error(`Error fetching: ${error}`);
            return of(gradeLessonActions.getGradeLessonFailure({ error }));
          }),
        );
      }),
    ),
  );

  // Effect to create a new lesson
  create$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(gradeLessonActions.create),
      debounceTime(500),
      switchMap(({ groupId, lessonId, gradeLesson }) => {
        const createLesson = {
          ...gradeLesson,
          created: Timestamp.now(),
          modified: Timestamp.now(),
        };

        return from(
          this.gradeLessonService.create(groupId, lessonId, createLesson),
        ).pipe(
          switchMap((lessonId) => {
            const localGradeLesson: GradeLesson = {
              ...gradeLesson,
              id: lessonId,
              created: Timestamp.fromDate(moment(gradeLesson.created).toDate()),
              modified: Timestamp.fromDate(
                moment(gradeLesson.modified).toDate(),
              ),
            };

            // Success notification and navigation
            this.toastr.success(
              'The lesson has been created successfully.',
              'Lesson Created',
            );
            // this.router.navigate(['school', groupId, 'lessons', lesson.id]);
            return of(
              gradeLessonActions.createSuccess({ lesson: localGradeLesson }),
            );
          }),
          catchError((error) => {
            this.logger.error(`Error creating lesson: ${error.toString()}`);
            this.toastr.error(
              `An error occurred during GradeLesson Creation. Please try again later.`,
              'Error Creating Lesson',
            );
            return of(gradeLessonActions.createFailure({ error }));
          }),
        );
      }),
    ),
  );

  // Effect to update a lesson
  update$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(gradeLessonActions.update),
      mergeMap(({ groupId, lessonId, gradeLesson }) => {
        const updateLesson = {
          ...gradeLesson,
          modified: serverTimestamp() as Timestamp,
        };
        return from(
          this.gradeLessonService.update(groupId, lessonId, updateLesson),
        ).pipe(
          mergeMap(() => {
            const localGradeLesson: GradeLesson = {
              ...gradeLesson,
              id: lessonId,
              modified: Timestamp.fromDate(
                moment(gradeLesson.modified).toDate(),
              ),
            };
            return of(
              gradeLessonActions.updateSuccess({ lesson: localGradeLesson }),
            );
          }),
          catchError((error) => {
            this.logger.error(`Error updating lesson: ${error}`);
            this.toastr.error(
              `An error occurred while updating the lesson details. Please try again later.`,
              'Error Updating Lesson',
            );
            return of(gradeLessonActions.updateFailure({ error }));
          }),
        );
      }),
    ),
  );

  getGradeLessonStats$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(groupActions.select, gradeLessonActions.loadGradeLessonStats),
      withLatestFrom(this.store.select(groupSelector.getSelectedGroup)),
      switchMap(([{ groupId }, group]) => {
        this.logout$.next();
        if (group?.groupType !== GroupType.school) return EMPTY;
        if (
          !intersects(group?.memberType as SchoolMemberType[], [
            SchoolMemberType.president,
            SchoolMemberType.principal,
          ])
        ) {
          return EMPTY;
        }
        if (group == undefined) return EMPTY;
        return this.afs
          .collection('groups')
          .doc(group.id)
          .collection<any>('lesson_stats')
          .doc(`${group.id}_${group.academicYear}`)
          .get()
          .pipe(takeUntil(this.logout$))
          .pipe(
            switchMap((snapshot) => {
              if (!snapshot.exists) return EMPTY;
              // console.log(`getGroupStatistics$ :: Stats found.`);
              const record: any = snapshot.data();
              let g: Group = {
                id: group.id,
                lessonStats: record,
                lessonStatsLoaded: true,
              } as Group;

              // console.log(
              //   `getGradeLessonStats$ :: Stats to store: ${JSON.stringify(g)}`
              // );
              return of(groupActions.getLessonStatsDone({ group: g }));
            }),
            catchError((error) => {
              this.logger.error(
                `getGradeLessonStats$ :: Error: ${JSON.stringify(error)}`,
              );
              return of(groupActions.getLessonStatsDone({ group: null }));
            }),
          );
      }),
    ),
  );

  // effect to delete a lesson
  delete$ = createEffect(() =>
    this.actions$.pipe(
      ofType(gradeLessonActions.deleteAction),
      switchMap(({ groupId, id }) =>
        this.gradeLessonService
          .delete(groupId, id)
          .then(() => {
            this.toastr.success(
              'The lesson has been deleted successfully.',
              'GradeLesson Deleted',
            );
            return gradeLessonActions.deleteSuccess({ id: id });
          })
          .catch((error: any) => {
            this.logger.error(`Error deleting lesson: ${error}`);
            this.toastr.error(
              `An error occurred, '${error}'. Please try again later.`,
              'Error Deleting GradeLesson',
            );
            return gradeLessonActions.deleteFailure({ error });
          }),
      ),
    ),
  );

  clearGroupGradeLessons$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(groupActions.clearFromStore),
      switchMap(({ id }) =>
        this.store
          .select(gradeLessonSelector.getGradeLessonIdsByGroup(id))
          .pipe(
            take(1),
            switchMap((lessons) => {
              if (lessons.length > 0) {
                return of(gradeLessonActions.clearFromStore({ ids: lessons }));
              }
              return EMPTY;
            }),
          ),
      ),
    ),
  );

  clearGradeLessons$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(userActions.logoutSuccess),
      switchMap(() => {
        this.logout$.next();
        return of(gradeLessonActions.clear());
      }),
    ),
  );
}
